/**
 * What a finished run actually produced.
 *
 * `result` is freeform JSON a job posts to `/finish` (see `sanctum-agent`'s
 * `emit_finish`) -- this deliberately reads only three keys out of it,
 * `branch`, `report` (the agent's own account of the run, schema-checked and
 * rendered as plain text) and, via the backend-built `mrUrl`, `mr_iid`, and
 * ignores every other key rather than dumping the object. A missing or
 * malformed `result`/`branch`/`report` renders nothing, never an error.
 *
 * A `bot`-identity run never writes code (`identity_service.py`'s
 * `may_write_code` is unconditionally `false` for bot mode) and so never
 * has a branch, regardless of what `result` happens to contain -- this
 * says so plainly instead of showing an empty field, which would read as
 * a bug rather than the intended outcome. Its report is still shown.
 */

import type { ReactElement } from 'react';
import { RunReportSchema, type RunReport } from '../schemas/agentRun.schemas';
import type { IdentityMode } from '../types/agentRun.types';

interface RunResultProps {
  identityMode: IdentityMode;
  result: Record<string, unknown> | null;
  mrUrl: string | null;
}

function stringField(result: Record<string, unknown> | null, key: string): string | undefined {
  if (result == null) return undefined;
  const value = result[key];
  return typeof value === 'string' ? value : undefined;
}

function parseReport(result: Record<string, unknown> | null): RunReport | null {
  if (result == null) return null;
  const parsed = RunReportSchema.safeParse(result['report']);
  return parsed.success ? parsed.data : null;
}

interface ReportListProps {
  title: string;
  items: ReactElement[];
}

function ReportList({ title, items }: ReportListProps): ReactElement | null {
  if (items.length === 0) return null;
  return (
    <>
      <h3 className="run-report__heading">{title}</h3>
      <ul className="run-report__list">{items}</ul>
    </>
  );
}

// Plain text only (React escapes it): the report is model-generated.
function ReportView({ report }: { report: RunReport }): ReactElement {
  return (
    <section className="run-report">
      <h3 className="run-report__heading">Summary</h3>
      <p className="run-report__summary">{report.summary}</p>
      <ReportList
        title="Addressed"
        items={report.addressed.map((text, i) => <li key={i}>{text}</li>)}
      />
      <ReportList
        title="Not addressed"
        items={report.not_addressed.map((entry, i) => (
          <li key={i}>
            <strong>{entry.item}</strong>: {entry.reason}
          </li>
        ))}
      />
      <ReportList
        title="Needs a person"
        items={report.needs_human.map((text, i) => <li key={i}>{text}</li>)}
      />
    </section>
  );
}

export function RunResult({ identityMode, result, mrUrl }: RunResultProps): ReactElement | null {
  const report = parseReport(result);

  if (identityMode === 'bot') {
    return (
      <>
        <p className="run-detail__result">
          This run is read-only — it will not produce a branch.
        </p>
        {report != null && <ReportView report={report} />}
      </>
    );
  }

  const branch = stringField(result, 'branch');
  if (branch == null && mrUrl == null && report == null) return null;

  return (
    <>
      {(branch != null || mrUrl != null) && (
        <p className="run-detail__result">
          {branch != null && (
            <span>
              Branch: <code>{branch}</code>
            </span>
          )}
          {branch != null && mrUrl != null && ' · '}
          {mrUrl != null && (
            <a href={mrUrl} target="_blank" rel="noreferrer">
              View merge request
            </a>
          )}
        </p>
      )}
      {report != null && <ReportView report={report} />}
    </>
  );
}
